import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LeftSidebar from './LeftSidebar';
import Topbar from './Topbar';
import Footer from './Footer';
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from '../Config';
import Swal from "sweetalert2";

const ProfileEdit = () => {
  const navigate = useNavigate();
   const id = localStorage.getItem('user_id');
   //console.log("Editing profile for user ID:", id);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state aligned with backend
  const [membership, setMembership] = useState('');
  const [firmName, setFirmName] = useState('');
  const [address, setAddress] = useState('');
  const [firmMobile, setFirmMobile] = useState('');
  const [area, setArea] = useState('');
  const [membershipStartDate, setMembershipStartDate] = useState('');
  const [membershipEndDate, setMembershipEndDate] = useState('');

  // Optional (not sent unless you wire it on backend)
  const [firmAlternateMobile, setFirmAlternateMobile] = useState('');

  // Representatives
  const [rep1Name, setRep1Name] = useState('');
  const [rep1Mobile, setRep1Mobile] = useState('');
  const [rep2Name, setRep2Name] = useState('');
  const [rep2Mobile, setRep2Mobile] = useState('');
  const [rep3Name, setRep3Name] = useState('');
  const [rep3Mobile, setRep3Mobile] = useState('');
  const [rep4Name, setRep4Name] = useState('');
  const [rep4Mobile, setRep4Mobile] = useState('');
  const [rep5Name, setRep5Name] = useState('');
  const [rep5Mobile, setRep5Mobile] = useState('');

  // Snapshot for Cancel
  const [snapshot, setSnapshot] = useState(null);

  // Structure helpers to grab/set all fields easily
  const readState = () => ({
    membershipStartDate,membershipEndDate,membership,firmName, address, firmMobile, area,
    firmAlternateMobile,
    rep1Name, rep1Mobile, rep2Name, rep2Mobile,
    rep3Name, rep3Mobile, rep4Name, rep4Mobile,
    rep5Name, rep5Mobile,
  });

  const writeState = (s) => {
    setMembershipStartDate(s.membershipStartDate ?? '');
    setMembershipEndDate(s.membershipEndDate ?? '');
    setMembership(s.membership ?? '');
    setFirmName(s.firmName ?? '');
    setAddress(s.address ?? '');
    setFirmMobile(s.firmMobile ?? '');
    setArea(s.area ?? '');
    setFirmAlternateMobile(s.firmAlternateMobile ?? '');

    setRep1Name(s.rep1Name ?? '');
    setRep1Mobile(s.rep1Mobile ?? '');
    setRep2Name(s.rep2Name ?? '');
    setRep2Mobile(s.rep2Mobile ?? '');
    setRep3Name(s.rep3Name ?? '');
    setRep3Mobile(s.rep3Mobile ?? '');
    setRep4Name(s.rep4Name ?? '');
    setRep4Mobile(s.rep4Mobile ?? '');
    setRep5Name(s.rep5Name ?? '');
    setRep5Mobile(s.rep5Mobile ?? '');
  };

  useEffect(() => {
    const fetchData = async () => {
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${BASE_URL}/api/profile_view/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const m = resp.data.list || {};
        console.log("Fetched member data:", m.name);

        // Map backend -> UI. Adjust keys as per actual payload.
        const initial = {
            membershipStartDate: m.membership_start_date || m.payment_date || '',
            membershipEndDate: m.membership_end_date || '',
            membership: m.membership || '',
          firmName: m.name || '',
          address: m.address || '',
          firmMobile: m.mobile || '',
          area: m.area || '',

          firmAlternateMobile: m.alternate_contact_no || m.mobile_alt || '',

          rep1Name: m.rep_name_1 || '',
          rep1Mobile: m.rep_mobile_1 || '',
          rep2Name: m.rep_name_2 || '',
          rep2Mobile: m.rep_mobile_2 || '',
          rep3Name: m.rep_name_3 || '',
          rep3Mobile: m.rep_mobile_3 || '',
          rep4Name: m.rep_name_4 || '',
          rep4Mobile: m.rep_mobile_4 || '',
          rep5Name: m.rep_name_5 || '',
          rep5Mobile: m.rep_mobile_5 || '',
        };

        writeState(initial);
        setSnapshot(initial);
      } catch (error) {
        console.error("Error fetching member data", error);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load member data.' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, BASE_URL]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const startEdit = () => {
    setSnapshot(readState()); // capture current values
    setIsEditing(true);
  };

  const cancelEdit = () => {
    if (snapshot) writeState(snapshot); // revert to snapshot
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');

      // Build representatives array for backend
      const representatives = [
        { name: rep1Name, mobile: rep1Mobile },
        { name: rep2Name, mobile: rep2Mobile },
        { name: rep3Name, mobile: rep3Mobile },
        { name: rep4Name, mobile: rep4Mobile },
        { name: rep5Name, mobile: rep5Mobile },
      ].filter(r => (r.name && r.name.trim()) || (r.mobile && r.mobile.trim()));

      // Payload aligned with your member_update validation
      const payload = {
        id: Number(id),
        firmName,
        firmMobile,
        address,
        area,
        representatives
      };

      const res = await fetch(`${BASE_URL}/api/profile_update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      

      if (res.ok && data.message) {
        // If API returns updated record, map it back; otherwise keep local changes
        const updated = (() => {
          const m = data.list || {}; // adjust if backend returns updated list/item
          if (!Object.keys(m).length) return readState();
          return {
            firmName: m.name || firmName,
            address: m.address || address,
            firmMobile:  m.mobile || firmMobile,
            area: m.area ?? area,

            firmAlternateMobile: m.alternate_contact_no || m.mobile_alt || firmAlternateMobile,

            rep1Name: m.rep_name_1 ?? rep1Name,
            rep1Mobile: m.rep_mobile_1 ?? rep1Mobile,
            rep2Name: m.rep_name_2 ?? rep2Name,
            rep2Mobile: m.rep_mobile_2 ?? rep2Mobile,
            rep3Name: m.rep_name_3 ?? rep3Name,
            rep3Mobile: m.rep_mobile_3 ?? rep3Mobile,
            rep4Name: m.rep_name_4 ?? rep4Name,
            rep4Mobile: m.rep_mobile_4 ?? rep4Mobile,
            rep5Name: m.rep_name_5 ?? rep5Name,
            rep5Mobile: m.rep_mobile_5 ?? rep5Mobile,
          };
        })();

        writeState(updated);
        setSnapshot(updated);
        setIsEditing(false);

        Swal.fire({ icon: 'success', title: 'Success', text: data.message });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: data.error || data.message || 'Update failed.' });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred during update.' });
    } finally {
      setIsSaving(false);
    }
  };

  const Plain = ({ children }) => (
    <div className="form-control-plaintext">{children || '-'}</div>
  );

  // Calculate membership dates
  let isExpired = false;
  let membershipStart = 'Not Available';
  let membershipEnd = 'Not Available';

  if (membershipStartDate && membershipEndDate) {
    const start = new Date(membershipStartDate);
    const end = new Date(membershipEndDate);
    if (!Number.isNaN(start.getTime())) {
      membershipStart = start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    if (!Number.isNaN(end.getTime())) {
      membershipEnd = end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const today = new Date();
      isExpired = today > end;
    }
  } else if (membershipStartDate) {
    // Calculate fixed fiscal year range (1 April to 31 March)
    const d = new Date(membershipStartDate);
    if (!Number.isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = d.getMonth() + 1; // 1-12
      let startYear, endYear;
      if (month >= 4) {
        startYear = year;
        endYear = year + 1;
      } else {
        startYear = year - 1;
        endYear = year;
      }
      const start = new Date(`${startYear}-04-01`);
      const end = new Date(`${endYear}-03-31`);
      
      membershipStart = start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      membershipEnd = end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      
      const today = new Date();
      isExpired = today > end;
    } else {
      isExpired = true;
    }
  } else {
    isExpired = true;
  }

  return (
    <div id="page-top">
      <div id="wrapper">
        <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <Topbar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <div className="container-fluid">
              <div className='col-md-12'>
                <div className='card'>
                  <div className='card-header py-3' style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <div className='row'>
                      <div className='col-md-6 d-flex left-content-end'>
                        <h6 className="font-weight-bold text-primary left">
                          {isEditing ? 'Edit Member' : 'Member Details'} - MEMBERSHIP NO.: {membership || 'Not Generated'}
                        </h6>
                      </div>
                      <div className='col-md-6 d-flex justify-content-end align-items-center gap-2'>
                        {!loading && !isEditing && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={startEdit}
                          >
                            Edit
                          </button>
                        )}
                        {isEditing && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={cancelEdit}
                              disabled={isSaving}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              form="memberForm"
                              className="btn btn-sm btn-primary"
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                          </>
                        )}
                        <i className="fa fa-arrow-left" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} />
                      </div>
                    </div>
                  </div>

                 <section className="py-5" style={{ background: "linear-gradient(135deg,#e0f7fa,#fce4ec)" }}>
  <div className="container">
    <div className="about-wrap2 position-relative">
      <div className="row align-items-start justify-content-center">
        {loading ? (
          <div className="p-4">Loading...</div>
        ) : (
          <form
            id="memberForm"
            onSubmit={handleSubmit}
            className="mx-auto bg-white p-4 p-md-5 rounded-4 shadow-lg"
            style={{ maxWidth: "950px" }}
          >
            {/* Header */}
            <div
              className="d-flex justify-content-between align-items-center p-3 rounded-3 mb-4"
              style={{
                background: "linear-gradient(90deg,#42a5f5,#478ed1)",
                color: "white",
              }}
            >
              <h4 className="mb-0 fw-bold">Member Profile</h4>
              {!isEditing && (
                <button
                  type="button"
                  className="btn btn-light btn-sm rounded-pill shadow-sm"
                  onClick={() => setIsEditing(true)}
                >
                  ✏️ Edit
                </button>
              )}
            </div>

            {/* Membership Information */}
            <div className="border rounded-3 p-4 mb-4" style={{ background: isExpired ? "#fff5f5" : "#f0fdf4", borderLeft: isExpired ? "5px solid #dc3545" : "5px solid #198754" }}>
              <h5 className="fw-semibold mb-3" style={{ color: isExpired ? "#dc3545" : "#198754" }}>🪪 Membership Information</h5>
              <div className="row g-4 text-left">
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-muted">Status</label>
                  <div>
                    {isExpired ? (
                      <span className="badge bg-danger text-white px-3 py-2 rounded-pill">Expired / Renewal Due</span>
                    ) : (
                      <span className="badge bg-success text-white px-3 py-2 rounded-pill">Active</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-muted">Start Date</label>
                  <div className="fw-semibold text-dark">{membershipStart}</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-muted">End Date (Renewal Date)</label>
                  <div className="fw-semibold text-dark">{membershipEnd}</div>
                </div>
              </div>
            </div>

            {/* Firm Details */}
            <div className="border rounded-3 p-4 mb-4" style={{ background: "#f9fbff" }}>
              <h5 className="fw-semibold text-primary mb-3">🏢 Firm Details</h5>
              <div className="row g-4 text-left">
                {/* Firm Name */}
                <div className="col-md-12">
                  <label className="form-label fw-semibold">Firm Name</label>
                  {!isEditing ? (
                    <p className="text-dark">{firmName}</p>
                  ) : (
                    <input type="text" className="form-control" value={firmName} readOnly />
                  )}
                </div>

                {/* Address */}
                <div className="col-md-12">
                  <label className="form-label fw-semibold">Address</label>
                  {!isEditing ? (
                    <p className="text-dark">{address}</p>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                    />
                  )}
                </div>
                
                {/* Firm Mobile */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Firm Mobile</label>
                  {!isEditing ? (
                    <p className="text-dark">{firmMobile}</p>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={firmMobile}
                      maxLength={15}
                      onChange={(e) =>
                        setFirmMobile(e.target.value.replace(/\D/g, "").slice(0, 15))
                      }
                      required
                    />
                  )}
                </div>

                {/* Firm Alternate Mobile */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Firm Alternate Mobile</label>
                  {!isEditing ? (
                    <p className="text-dark">{firmAlternateMobile}</p>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={firmAlternateMobile}
                      maxLength={15}
                      onChange={(e) =>
                        setFirmAlternateMobile(e.target.value.replace(/\D/g, "").slice(0, 15))
                      }
                    />
                  )}
                </div>

                {/* Area */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Area</label>
                  {!isEditing ? (
                    <p className="text-dark">{area}</p>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      required
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Contact Persons */}
            <div className="border rounded-3 p-4 mb-4" style={{ background: "rgb(241 241 241)" }}>
              <h5 className="fw-semibold text-warning mb-3">👥 Contact Persons</h5>
              {[1, 2, 3, 4, 5].map((i) => (
                <div className="row g-3 mb-3" key={i}>
                  <div className="col-md-6">
                    <div className="box">
                    <label className="form-label"><strong>Representative Name-{i}</strong></label>
                    {!isEditing ? (
                      <p className="text-dark">{eval(`rep${i}Name`) || "-"}</p>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={eval(`rep${i}Name`)}
                        onChange={(e) => eval(`setRep${i}Name(e.target.value)`)}
                      />
                    )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="box">
                    <label className="form-label"><strong>Representative Mobile-{i}</strong></label>
                    {!isEditing ? (
                      <p className="text-dark">{eval(`rep${i}Mobile`) || "-"}</p>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={eval(`rep${i}Mobile`)}
                        maxLength={15}
                        onChange={(e) =>
                          eval(
                            `setRep${i}Mobile(e.target.value.replace(/\\D/g, '').slice(0, 15))`
                          )
                        }
                      />
                    )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Save / Cancel Buttons */}
            {isEditing && (
              <div className="text-center mt-4">
                <button
                  type="submit"
                  className="btn btn-success px-5 py-2 rounded-pill shadow-sm me-2"
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Update"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary px-5 py-2 rounded-pill"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  </div>
</section>

                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;
